import React, { useState, useEffect, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import { rocketOutline, checkmarkCircle } from 'ionicons/icons';
import '../MarketplacePage/MarketplacePage.css';
import './LoopsSection.css';

const ICON_BASE = 'https://zibby.dev';

const FEATURED_LOOPS = [
  {
    slug: 'zibby-copilot',
    name: 'Zibby Copilot',
    authorType: 'official',
    icon: `${ICON_BASE}/marketplace-icons/zibby-copilot.png`,
    description: 'The official Zibby copilot — run, deploy, and manage your workspace by just asking.',
  },
  {
    slug: 'sentry-triage',
    name: 'Sentry Triage & Fix',
    authorType: 'official',
    icon: `${ICON_BASE}/marketplace-icons/sentry-triage.png`,
  },
  {
    slug: 'github-code-review',
    name: 'GitHub Code Review',
    authorType: 'official',
    icon: `${ICON_BASE}/marketplace-icons/github-code-review.png`,
  },
  {
    slug: 'gitlab-code-review',
    name: 'GitLab Code Review',
    authorType: 'official',
    icon: `${ICON_BASE}/marketplace-icons/gitlab-code-review.png`,
  },
];

const FEATURED_PALETTES = {
  'zibby-copilot':      { from: '#4F46E5', to: '#7C3AED', accent: '#a78bfa' },
  'chat-ops-bot':       { from: '#0ea5e9', to: '#6366f1', accent: '#7dd3fc' },
  'sentry-triage':      { from: '#6E56CF', to: '#362D59', accent: '#c4b5fd' },
  'github-code-review': { from: '#2D333B', to: '#0F1318', accent: '#9aa7b4' },
  'gitlab-code-review': { from: '#FC6D26', to: '#8133D6', accent: '#ffc7a3' },
};

const FEATURED_TAGLINES = {
  'zibby-copilot':
    'Run, deploy, and manage your entire workspace by just asking. It handles Sentry issues, flaky tests, deployments — whatever you throw at it.',
  'sentry-triage':
    'Watches your Sentry issues hourly — reproduces each one, finds the root cause, then ships an auto-fix and opens a PR for review.',
  'github-code-review':
    'Reviews every pull request the moment it opens — flags bugs, security issues, and risky changes, then posts inline comments your team can act on.',
  'gitlab-code-review':
    'Reviews every merge request automatically — catches bugs, security holes, and regressions, then leaves inline notes right where they matter.',
};

const hashHue = (str) => {
  let h = 0;
  for (let i = 0; i < (str || '').length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) % 360;
  }
  return h;
};

const getFeaturedPalette = (entry) => {
  const slug = entry?.slug || '';
  if (FEATURED_PALETTES[slug]) return FEATURED_PALETTES[slug];
  const hue = hashHue(slug || entry?.name || 'zibby');
  const base = 200 + (hue % 80);
  return {
    from: `hsl(${base}, 70%, 42%)`,
    to: `hsl(${(base + 40) % 360}, 72%, 30%)`,
    accent: `hsl(${base}, 85%, 78%)`,
  };
};

const getFeaturedDemo = (entry) => {
  if (entry?.slug === 'zibby-copilot') {
    return [
      { prompt: '@Zibby fix my flaky test in CircleCI', reply: 'Root-caused + fixed — PR opened' },
      { prompt: '@Zibby fix the Sentry issue', reply: 'Triaged + patched — PR opened' },
    ];
  }
  if (entry?.slug === 'sentry-triage') {
    return [
      { prompt: '@Zibby triage SENTRY-4821', reply: 'Root-caused + patched — PR opened' },
      { prompt: '@Zibby fix the prod 500 spike', reply: 'Triaged + fix shipped — PR opened' },
    ];
  }
  const name = entry?.name || entry?.slug || 'this agent';
  return [{ prompt: `@Zibby run ${name}`, reply: 'On it — done' }];
};

const SLACK_DEMO_SLUGS = new Set(['zibby-copilot', 'chat-ops-bot']);

const FEATURED_GRAPHS = {
  'sentry-triage': {
    title: 'sentry-triage · run',
    label: 'Sentry triage workflow: alert, reproduce and triage, root cause found, then patch with a PR opened.',
    nodes: [
      { label: 'Sentry alert', sub: 'TypeError · prod' },
      { label: 'Reproduce & triage', sub: 'stack trace + context' },
      { label: 'Root cause found', sub: 'null deref in handler' },
      { label: 'Patch → PR opened', sub: '#482 — ready for review', done: true },
    ],
  },
  'github-code-review': {
    title: 'github-code-review · run',
    label: 'GitHub code-review workflow: pull request opened, fetch the diff and context, review for bugs and security, then inline comments posted.',
    nodes: [
      { label: 'Pull request opened', sub: 'feat/checkout · +214 −38' },
      { label: 'Fetch diff & context', sub: 'changed files + history' },
      { label: 'Review', sub: 'bugs · security · style' },
      { label: 'Inline comments posted', sub: '6 findings · 1 blocker', done: true },
    ],
  },
  'gitlab-code-review': {
    title: 'gitlab-code-review · run',
    label: 'GitLab code-review workflow: merge request opened, fetch the diff and context, review for bugs and security, then inline notes posted.',
    nodes: [
      { label: 'Merge request opened', sub: 'feat/billing · +176 −22' },
      { label: 'Fetch diff & context', sub: 'changed files + history' },
      { label: 'Review', sub: 'bugs · security · style' },
      { label: 'Inline notes posted', sub: '5 findings · approved', done: true },
    ],
  },
};

// ── Demo visuals (unchanged from original) ──

const WorkflowCardIcon = ({ icon, label }) => {
  const [failed, setFailed] = useState(false);
  const letter = (label || '?').slice(0, 1).toUpperCase();
  if (!icon || failed) {
    return <span className="marketplace-card-icon marketplace-card-icon--fallback">{letter}</span>;
  }
  return (
    <img
      src={icon}
      alt=""
      className="marketplace-card-icon"
      width={56}
      height={56}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

const FeaturedSlackWindow = ({ demos, palette }) => {
  const list = Array.isArray(demos) && demos.length ? demos : [{ prompt: '', reply: '' }];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (list.length < 2) return undefined;
    const t = setInterval(() => setI((c) => (c + 1) % list.length), 8000);
    return () => clearInterval(t);
  }, [list.length]);
  const demo = list[i % list.length];
  return (
    <div
      className="mkt-slack"
      style={{ '--slack-accent': palette.accent }}
      role="img"
      aria-label={`Slack chat demo: a user types "${demo.prompt}" and Zibby replies "${demo.reply}".`}
    >
      <div className="mkt-slack-chrome" aria-hidden="true">
        <span className="mkt-slack-dots">
          <span className="mkt-slack-dot mkt-slack-dot--r" />
          <span className="mkt-slack-dot mkt-slack-dot--y" />
          <span className="mkt-slack-dot mkt-slack-dot--g" />
        </span>
        <span className="mkt-slack-channel">
          <span className="mkt-slack-channel-icon">#</span>
          zibby-ops
        </span>
      </div>
      <div className="mkt-slack-thread mkt-slack-thread--rotate" aria-hidden="true" key={i}>
        <div className="mkt-slack-msg">
          <span className="mkt-slack-avatar mkt-slack-avatar--user">Y</span>
          <span className="mkt-slack-body">
            <span className="mkt-slack-meta">
              <span className="mkt-slack-name">You</span>
              <span className="mkt-slack-time">9:41 AM</span>
            </span>
            <span className="mkt-slack-text">{demo.prompt}</span>
          </span>
        </div>
        <div className="mkt-slack-msg mkt-slack-msg--bot">
          <span className="mkt-slack-avatar mkt-slack-avatar--bot" aria-hidden="true">
            <IonIcon icon={rocketOutline} />
          </span>
          <span className="mkt-slack-body">
            <span className="mkt-slack-meta">
              <span className="mkt-slack-name">Zibby</span>
              <span className="mkt-slack-appbadge">APP</span>
              <span className="mkt-slack-time">9:41 AM</span>
            </span>
            <span className="mkt-slack-text mkt-slack-text--bot">
              <IonIcon icon={checkmarkCircle} className="mkt-slack-check" aria-hidden="true" />
              {demo.reply}
            </span>
            <span className="mkt-slack-skeleton">
              <span className="mkt-slack-skel-line" />
              <span className="mkt-slack-skel-line mkt-slack-skel-line--short" />
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

const FeaturedGenericDemo = ({ demo }) => (
  <div className="mkt-hero-demo" aria-hidden="true">
    <div className="mkt-hero-bubble mkt-hero-bubble--user">{demo.prompt}</div>
    <div className="mkt-hero-bubble mkt-hero-bubble--bot">
      <IonIcon icon={checkmarkCircle} className="mkt-hero-bubble-check" />
      {demo.reply}
    </div>
  </div>
);

const FeaturedGraphDemo = ({ palette, nodes, title, label }) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((c) => (c + 1) % nodes.length), 1400);
    return () => clearInterval(t);
  }, [nodes.length]);
  return (
    <div
      className="mkt-graph"
      style={{ '--graph-accent': palette.accent }}
      role="img"
      aria-label={label || `${title} workflow`}
    >
      <div className="mkt-graph-chrome" aria-hidden="true">
        <span className="mkt-slack-dots">
          <span className="mkt-slack-dot mkt-slack-dot--r" />
          <span className="mkt-slack-dot mkt-slack-dot--y" />
          <span className="mkt-slack-dot mkt-slack-dot--g" />
        </span>
        <span className="mkt-graph-title">{title}</span>
      </div>
      <div className="mkt-graph-body" aria-hidden="true">
        <span className="mkt-graph-rail"><span className="mkt-graph-pulse" /></span>
        {nodes.map((n, i) => (
          <div
            key={n.label}
            className={`mkt-graph-node${n.done ? ' mkt-graph-node--done' : ''}${i === active ? ' is-active' : ''}`}
            style={{ animationDelay: `${0.1 + i * 0.13}s` }}
          >
            <span className="mkt-graph-dot">
              {n.done ? <IonIcon icon={checkmarkCircle} /> : null}
            </span>
            <span className="mkt-graph-node-body">
              <span className="mkt-graph-node-label">{n.label}</span>
              <span className="mkt-graph-node-sub">{n.sub}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Carousel — original logic, index now controlled from parent ──

const LoopsCarousel = ({ items, activeIndex, onIndexChange, onSelect, ctaLabel = 'Try it' }) => {
  const [paused, setPaused] = useState(false);
  const count = items.length;
  const multi = count > 1;

  const go = useCallback(
    (next) => onIndexChange(((next % count) + count) % count),
    [count, onIndexChange],
  );
  const prev = useCallback(() => go(activeIndex - 1), [go, activeIndex]);
  const next = useCallback(() => go(activeIndex + 1), [go, activeIndex]);

  useEffect(() => {
    if (!multi || paused) return undefined;
    const t = setInterval(() => onIndexChange((cur) => (cur + 1) % count), 8000);
    return () => clearInterval(t);
  }, [multi, paused, count, onIndexChange]);

  const onKeyDown = useCallback(
    (e) => {
      if (!multi) return;
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    },
    [multi, prev, next],
  );

  const entry = items[activeIndex] || items[0];
  const palette = getFeaturedPalette(entry);
  const demos = getFeaturedDemo(entry);
  const tagline = (FEATURED_TAGLINES[entry.slug] || entry.tagline || entry.description || '').slice(0, 170);

  return (
    <div
      className="mkt-hero-carousel"
      role="group"
      aria-roledescription="carousel"
      aria-label="Featured agents"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={onKeyDown}
    >
      <div
        key={activeIndex}
        className="mkt-hero"
        style={{
          '--hero-from': palette.from,
          '--hero-to': palette.to,
          '--hero-accent': palette.accent,
        }}
        aria-roledescription="slide"
        aria-label={`${entry.name || entry.slug}${multi ? ` (${activeIndex + 1} of ${count})` : ''}`}
      >
        <div className="mkt-hero-left">
          <span className="mkt-hero-icon-wrap">
            <WorkflowCardIcon icon={entry.icon} label={entry.name || entry.slug} />
          </span>
          <div className="mkt-hero-text">
            <span className="mkt-hero-eyebrow">
              {entry.authorType === 'official' ? 'Official agent' : 'Agent'}
            </span>
            <h3 className="mkt-hero-title">{entry.name || entry.slug}</h3>
            {tagline && <p className="mkt-hero-subtitle">{tagline}</p>}
            <div className="mkt-hero-actions">
              <button
                type="button"
                className="mkt-hero-cta"
                onClick={() => onSelect(entry)}
                aria-label={`${ctaLabel} ${entry.name || entry.slug}`}
              >
                <IonIcon icon={rocketOutline} aria-hidden="true" />
                {ctaLabel}
              </button>
            </div>
          </div>
        </div>

        <div className="mkt-hero-right">
          {FEATURED_GRAPHS[entry.slug] ? (
            <FeaturedGraphDemo palette={palette} {...FEATURED_GRAPHS[entry.slug]} />
          ) : SLACK_DEMO_SLUGS.has(entry.slug) ? (
            <FeaturedSlackWindow demos={demos} palette={palette} />
          ) : (
            <FeaturedGenericDemo demo={demos[0]} />
          )}
        </div>
      </div>

      {multi && (
        <div className="mkt-hero-dots" role="tablist" aria-label="Featured agents">
          {items.map((it, i) => (
            <button
              key={it.slug}
              type="button"
              role="tab"
              aria-selected={i === activeIndex}
              aria-label={`Show ${it.name || it.slug}`}
              className={`mkt-hero-dot${i === activeIndex ? ' is-active' : ''}`}
              onClick={() => onIndexChange(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Section: 2-col split, left text syncs with carousel index ──

export default function LoopsSection() {
  const [index, setIndex] = useState(0);

  const entry = FEATURED_LOOPS[index] || FEATURED_LOOPS[0];
  const tagline = FEATURED_TAGLINES[entry.slug] || entry.description || '';

  return (
    <section className="loops-section reveal">
      {/* Section heading */}
      <div className="loops-head">
        <span className="loops-head-badge">Agents</span>
        <h2 className="loops-head-title">Your AI team, always running.</h2>
        <p className="loops-head-sub">
          Deploy an agent, connect your tools — it runs on its own schedule, forever.
        </p>
      </div>

      {/* 2-col split */}
      <div className="loops-split">
        {/* Left: big caption text — animates when index changes */}
        <div className="loops-split-left" key={index}>
          <span className="loops-split-eyebrow">Pre-built agents</span>
          <h3 className="loops-split-title">{entry.name}</h3>
          <p className="loops-split-desc">{tagline}</p>
          <a href="/marketplace" className="loops-split-cta">
            Browse all agents →
          </a>
        </div>

        {/* Right: original full slider, unchanged */}
        <div className="loops-split-right">
          <LoopsCarousel
            items={FEATURED_LOOPS}
            activeIndex={index}
            onIndexChange={setIndex}
            onSelect={() => { window.location.href = '/marketplace'; }}
            ctaLabel="Deploy"
          />
        </div>
      </div>
    </section>
  );
}
